import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from '@hq-core/models/app-settings';
import { DataResource } from '@hq-custom-types';
import {
    ApiCollectionResponse,
    ParameterBuilder,
    SearchState,
} from '@cia-front-end-apps/shared/api-interaction';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Brand } from '../models/brand';

@Injectable({ providedIn: 'root' })
export class BrandResource implements DataResource<Brand> {
    private readonly baseUrl: string;

    constructor(
        private http: HttpClient,
        private appSettings: AppSettings,
    ) {
        this.baseUrl = this.appSettings.apis.vendorData;
    }

    get(brandId: string): Observable<Brand> {
        return this.http.get<Brand>(`${this.baseUrl}/brands/${brandId}`);
    }

    searchBrands(term: string, pageSize = 15): Observable<Brand[]> {
        return this.brandSearch({ pageIndex: '0', pageSize: pageSize.toString(), searchParameter: term });
    }

    searchParentBrands(term: string, pageSize = 15): Observable<Brand[]> {
        return this.brandSearch({ pageIndex: '0', pageSize: pageSize.toString(), searchParameter: term, processed: 'true' });
    }

    private brandSearch(fromObject: Record<string, string>): Observable<Brand[]> {
        const params = new HttpParams({ fromObject });
        return this.http.get<ApiCollectionResponse<Brand>>(`${this.baseUrl}/brands`, { params }).pipe(
            map(response => response.items ?? []),
        );
    }

    rollUp(brandIds: number[], parentBrandId: number): Observable<Brand[]> {
        let params = new HttpParams({
            fromObject: { parentBrandId: parentBrandId.toString() },
        });
        params = ParameterBuilder.buildArrayParameter(brandIds, 'brandIds', params);
        return this.http.put<Brand[]>(`${this.baseUrl}/brands`, {}, { params });
    }

    create(sourceBrandId: number, brandName: string, itemIds: number[]): Observable<Brand> {
        return this.http.post<Brand>(`${this.baseUrl}/brands`, { brandName, sourceBrandId, itemIds });
    }

    process(brandIds: number[]): Observable<void> {
        let params = new HttpParams();
        params = ParameterBuilder.buildArrayParameter(brandIds, 'brandIds', params);
        return this.http.patch<void>(`${this.baseUrl}/brands`, {}, { params });
    }

    getAll(searchState: SearchState): Observable<ApiCollectionResponse<Brand>> {
        const url = `${this.baseUrl}/brands`;

        const params = new HttpParams({
            fromObject: {
                pageIndex: (searchState.pageIndex ?? 0).toString(),
                pageSize: (searchState.pageSize ?? 25).toString(),
                sortBy: searchState.sortBy ?? '',
                sortDirection: searchState.sortDirection ?? 'asc',
                searchParameter: searchState.search ?? '',
                processed: 'false',
            },
        });

        return this.http.get<ApiCollectionResponse<Brand>>(url, { params });
    }
}
